import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import { useTheme } from '../contexts/ThemeContext';
import { maxSlotBudget } from '../utils/event';
import { baht } from '../utils/format';
const CARTO_API_KEY = 'cb1_4cgr_1_925835cbe5676ada9fc2e05c';
import type { MusicEvent } from '../types/marketplace';
interface EventsMapProps {
  events: MusicEvent[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}
export function EventsMap({
  events,
  selectedId,
  onSelect
}: EventsMapProps) {
  const el = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const layer = useRef<L.LayerGroup | null>(null);
  const tiles = useRef<L.TileLayer | null>(null);
  const onSelectRef = useRef(onSelect);
  const fitted = useRef(false);
  const {
    resolved
  } = useTheme();
  onSelectRef.current = onSelect;
  useEffect(() => {
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
  }, []);
  useEffect(() => {
    if (!el.current) return;
    const m = L.map(el.current, {
      zoomControl: false
    }).setView([13.75, 100.53], 10);
    L.control.zoom({
      position: 'bottomright'
    }).addTo(m);
    layer.current = L.layerGroup().addTo(m);
    map.current = m;
    const ro = new ResizeObserver(() => m.invalidateSize());
    ro.observe(el.current);
    return () => {
      ro.disconnect();
      m.remove();
      map.current = null;
    };
  }, []);
  useEffect(() => {
    const m = map.current;
    if (!m) return;
    tiles.current?.remove();
    tiles.current = L.tileLayer(`https://{s}.basemaps.cartocdn.com/${resolved === 'dark' ? 'dark_all' : 'light_all'}/{z}/{x}/{y}{r}.png?api_key=${CARTO_API_KEY}`, {
      attribution: '&copy; OpenStreetMap &copy; CARTO',
      subdomains: 'abcd',
      maxZoom: 19
    }).addTo(m);
  }, [resolved]);
  useEffect(() => {
    const m = map.current;
    const group = layer.current;
    if (!m || !group) return;
    group.clearLayers();
    events.forEach(e => {
      const active = e.id === selectedId;
      const icon = L.divIcon({
        className: 'encore-pin-wrap',
        html: `<div style="display:flex;justify-content:center;align-items:flex-end;height:40px"><div class="encore-pin${active ? ' is-active' : ''}${e.featured ? ' is-featured' : ''}" role="button" aria-label="${e.name}">${baht(maxSlotBudget(e))}</div></div>`,
        iconSize: [96, 40],
        iconAnchor: [48, 40]
      });
      L.marker([e.lat, e.lng], {
        icon,
        zIndexOffset: active ? 1000 : 0,
        keyboard: true,
        title: e.name
      }).on('click', () => onSelectRef.current(e.id)).addTo(group);
    });
    if (!fitted.current && events.length) {
      m.fitBounds(L.latLngBounds(events.map(e => [e.lat, e.lng] as [number, number])), {
        padding: [60, 60]
      });
      fitted.current = true;
    }
  }, [events, selectedId]);
  useEffect(() => {
    const m = map.current;
    const e = events.find(x => x.id === selectedId);
    if (m && e) m.panTo([e.lat, e.lng], {
      animate: true,
      duration: 0.3
    });
  }, [selectedId, events]);
  return <div ref={el} className="h-full w-full" role="region" aria-label="Map of events" data-id="element-277" data-mp="2wq7hzf1q8qj6" />;
}